import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Calendar, Clock, Users, BookOpen, Building, Filter, Download, Upload, Printer } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSchoolYear } from '../contexts/SchoolYearContext';

// Styles d'impression
const printStyles = `
  @media print {
    .no-print { display: none !important; }
    .sidebar { display: none !important; }
    body { margin: 0; padding: 0; }
    .edt-grid { page-break-inside: avoid; }
    table { border-collapse: collapse; }
    th, td { border: 1px solid #000 !important; }
  }
`;

// Injecter les styles d'impression
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = printStyles;
  document.head.appendChild(styleSheet);
}

// Composant Modal pour créer/éditer une séance
const SessionModal = ({ open, onClose, session, onSave, onDelete, classes = [], subjects = [], teachers = [], rooms = [], timeslots = [], dayOfWeek, timeslotId }) => {
  const [formData, setFormData] = useState({
    class_id: '',
    subject_id: '',
    teacher_id: '',
    room_id: '',
    day_of_week: dayOfWeek || 1,
    timeslot_id: timeslotId || '',
    timeslot_start_id: timeslotId || '',
    timeslot_end_id: timeslotId || '',
    notes: '',
    color: ''
  });

  useEffect(() => {
    if (open) {
      if (session) {
        setFormData({
          class_id: session.class_id || '',
          subject_id: session.subject_id || '',
          teacher_id: session.teacher_id || '',
          room_id: session.room_id || '',
          day_of_week: session.day_of_week || dayOfWeek || 1,
          timeslot_id: session.timeslot_id || timeslotId || '',
          timeslot_start_id: session.timeslot_id || timeslotId || '',
          timeslot_end_id: session.timeslot_id || timeslotId || '',
          notes: session.notes || '',
          color: session.color || ''
        });
      } else {
        setFormData({
          class_id: '',
          subject_id: '',
          teacher_id: '',
          room_id: '',
          day_of_week: dayOfWeek || 1,
          timeslot_id: timeslotId || '',
          timeslot_start_id: timeslotId || '',
          timeslot_end_id: timeslotId || '',
          notes: '',
          color: ''
        });
      }
    }
  }, [open, session, dayOfWeek, timeslotId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.class_id || !formData.subject_id || !formData.teacher_id || !formData.timeslot_start_id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    // Si pas de créneau fin, utiliser le créneau début (bloc d'un seul créneau)
    const startId = formData.timeslot_start_id;
    const endId = formData.timeslot_end_id || formData.timeslot_start_id;
    
    // Vérifier que le créneau fin est après le créneau début
    if (startId && endId && timeslots.length > 0) {
      const startIndex = timeslots.findIndex(t => t.id === parseInt(startId));
      const endIndex = timeslots.findIndex(t => t.id === parseInt(endId));
      
      if (startIndex === -1 || endIndex === -1) {
        toast.error('Créneaux invalides');
        return;
      }
      
      if (endIndex < startIndex) {
        toast.error('Le créneau de fin doit être après le créneau de début');
        return;
      }
    }
    
    onSave({ ...formData, timeslot_start_id: startId, timeslot_end_id: endId });
  };

  const selectedSubject = subjects.find(s => s.id === formData.subject_id);
  const defaultColor = selectedSubject?.color || '#3B82F6';

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-md shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{session ? 'Modifier la séance' : 'Nouvelle séance'}</h3>
          <button className="btn" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="label">Classe *</label>
            <select
              className="input w-full"
              value={formData.class_id}
              onChange={(e) => setFormData({ ...formData, class_id: e.target.value })}
              required
            >
              <option value="">— Choisir une classe —</option>
              {classes.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="label">Matière *</label>
            <select
              className="input w-full"
              value={formData.subject_id}
              onChange={(e) => {
                const subject = subjects.find(s => s.id === e.target.value);
                setFormData({ 
                  ...formData, 
                  subject_id: e.target.value,
                  color: subject?.color || formData.color
                });
              }}
              required
            >
              <option value="">— Choisir une matière —</option>
              {(() => {
                // Dédupliquer par nom
                const seen = new Set();
                return subjects.filter(s => {
                  const key = (s.name || '').toLowerCase().trim();
                  if (!key || seen.has(key)) return false;
                  seen.add(key);
                  return true;
                }).map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ));
              })()}
            </select>
          </div>
          
          <div>
            <label className="label">Professeur *</label>
            <select
              className="input w-full"
              value={formData.teacher_id}
              onChange={(e) => setFormData({ ...formData, teacher_id: e.target.value })}
              required
            >
              <option value="">— Choisir un professeur —</option>
              {teachers.map(t => (
                <option key={t.id} value={t.id}>
                  {t.first_name && t.last_name ? `${t.first_name} ${t.last_name}` : t.username}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="label">Salle</label>
            <select
              className="input w-full"
              value={formData.room_id}
              onChange={(e) => setFormData({ ...formData, room_id: e.target.value })}
            >
              <option value="">— Aucune salle —</option>
              {(() => {
                // Dédupliquer par nom
                const seen = new Set();
                return rooms.filter(r => {
                  const key = (r.name || '').toLowerCase().trim();
                  if (!key || seen.has(key)) return false;
                  seen.add(key);
                  return true;
                }).map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ));
              })()}
            </select>
          </div>
          
          <div>
            <label className="label">Jour *</label>
            <select
              className="input w-full"
              value={formData.day_of_week}
              onChange={(e) => setFormData({ ...formData, day_of_week: parseInt(e.target.value) })}
              required
            >
              <option value="1">Lundi</option>
              <option value="2">Mardi</option>
              <option value="3">Mercredi</option>
              <option value="4">Jeudi</option>
              <option value="5">Vendredi</option>
            </select>
          </div>
          
          {!session ? (
            <>
              <div>
                <label className="label">Créneau horaire début *</label>
                <select
                  className="input w-full"
                  value={formData.timeslot_start_id}
                  onChange={(e) => {
                    const newStartId = e.target.value;
                    setFormData({ 
                      ...formData, 
                      timeslot_start_id: newStartId,
                      timeslot_id: newStartId,
                      // Si le créneau fin est avant le nouveau début, le réinitialiser
                      timeslot_end_id: formData.timeslot_end_id && (() => {
                        const startIndex = timeslots.findIndex(t => t.id === parseInt(newStartId));
                        const endIndex = timeslots.findIndex(t => t.id === parseInt(formData.timeslot_end_id));
                        return endIndex >= startIndex ? formData.timeslot_end_id : newStartId;
                      })() || newStartId
                    });
                  }}
                  required
                >
                  <option value="">— Choisir un créneau —</option>
                  {timeslots && Array.isArray(timeslots) && timeslots.length > 0 ? (
                    timeslots.map(t => (
                      <option key={t.id} value={t.id}>{t.label || `${t.start_time} - ${t.end_time}`}</option>
                    ))
                  ) : (
                    <option value="" disabled>Aucun créneau disponible</option>
                  )}
                </select>
              </div>
              
              <div>
                <label className="label">Créneau horaire fin *</label>
                <select
                  className="input w-full"
                  value={formData.timeslot_end_id}
                  onChange={(e) => {
                    const newEndId = e.target.value;
                    setFormData({ 
                      ...formData, 
                      timeslot_end_id: newEndId,
                      timeslot_id: formData.timeslot_start_id
                    });
                  }}
                  required
                >
                  <option value="">— Choisir un créneau —</option>
                  {timeslots && Array.isArray(timeslots) && timeslots.length > 0 ? (
                    (() => {
                      const startIndex = formData.timeslot_start_id 
                        ? timeslots.findIndex(t => t.id === parseInt(formData.timeslot_start_id))
                        : -1;
                      return timeslots
                        .filter((t, index) => startIndex === -1 || index >= startIndex)
                        .map(t => (
                          <option key={t.id} value={t.id}>{t.label || `${t.start_time} - ${t.end_time}`}</option>
                        ));
                    })()
                  ) : (
                    <option value="" disabled>Aucun créneau disponible</option>
                  )}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Sélectionnez le créneau de fin pour créer un bloc sur plusieurs créneaux. 
                  {formData.timeslot_start_id && formData.timeslot_end_id && formData.timeslot_start_id !== formData.timeslot_end_id && (() => {
                    const startIndex = timeslots.findIndex(t => t.id === parseInt(formData.timeslot_start_id));
                    const endIndex = timeslots.findIndex(t => t.id === parseInt(formData.timeslot_end_id));
                    const span = endIndex >= startIndex ? endIndex - startIndex + 1 : 1;
                    return ` Le bloc couvrira ${span} créneau${span > 1 ? 'x' : ''}.`;
                  })()}
                </p>
              </div>
            </>
          ) : (
            <div>
              <label className="label">Créneau horaire *</label>
              <select
                className="input w-full"
                value={formData.timeslot_id}
                onChange={(e) => setFormData({ ...formData, timeslot_id: e.target.value })}
                required
              >
                <option value="">— Choisir un créneau —</option>
                {timeslots && Array.isArray(timeslots) && timeslots.length > 0 ? (
                  timeslots.map(t => (
                    <option key={t.id} value={t.id}>{t.label || `${t.start_time} - ${t.end_time}`}</option>
                  ))
                ) : (
                  <option value="" disabled>Aucun créneau disponible</option>
                )}
              </select>
            </div>
          )}
          
          {(!timeslots || !Array.isArray(timeslots) || timeslots.length === 0) && (
            <p className="text-xs text-red-500 mt-1">Aucun créneau horaire défini. Ajoutez-en dans le panneau de configuration.</p>
          )}
          
          <div>
            <label className="label">Couleur</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                className="w-12 h-10 rounded border"
                value={formData.color || defaultColor}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              />
              <input
                type="text"
                className="input flex-1"
                value={formData.color || defaultColor}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                placeholder="#3B82F6"
              />
            </div>
          </div>
          
          <div>
            <label className="label">Notes</label>
            <textarea
              className="input w-full"
              rows="3"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Notes optionnelles..."
            />
          </div>
          
          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            {session && onDelete && (
              <button 
                type="button" 
                className="btn text-red-600 hover:bg-red-50" 
                onClick={() => {
                  if (window.confirm('Supprimer cette séance ?')) {
                    onDelete(session.id);
                  }
                }}
              >
                Supprimer
              </button>
            )}
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary">Valider</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Composant CRUD pour les matières
const SubjectsManager = ({ subjects, onRefresh }) => {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: '#3B82F6', default_duration: 1 });

  // Dédupliquer les matières par nom (garder le premier trouvé)
  const uniqueSubjects = React.useMemo(() => {
    const seen = new Map();
    return (subjects || []).filter(subject => {
      const key = (subject.name || '').toLowerCase().trim();
      if (!key || seen.has(key)) {
        return false;
      }
      seen.set(key, true);
      return true;
    });
  }, [subjects]);

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`/api/edt/subjects/${editing.id}`, formData);
        toast.success('Matière mise à jour');
      } else {
        await axios.post('/api/edt/subjects', formData);
        toast.success('Matière créée');
      }
      setShowModal(false);
      setEditing(null);
      setFormData({ name: '', color: '#3B82F6', default_duration: 1 });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    console.log('Suppression matière, ID:', id);
    if (!window.confirm('Supprimer cette matière ?')) return;
    try {
      const response = await axios.delete(`/api/edt/subjects/${id}`);
      console.log('Réponse suppression:', response.data);
      toast.success('Matière supprimée');
      onRefresh();
    } catch (error) {
      console.error('Erreur suppression matière:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">Matières</h3>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setFormData({ name: '', color: '#3B82F6', default_duration: 1 }); setShowModal(true); }}>
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>
      <div className="space-y-2">
        {uniqueSubjects.map(s => (
          <div key={s.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors">
            <div className="w-5 h-5 rounded flex-shrink-0 shadow-sm" style={{ backgroundColor: s.color }}></div>
            <span className="text-sm font-medium text-gray-800 flex-1 min-w-0 break-words pr-2">{s.name}</span>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button 
                type="button"
                className="p-1.5 rounded hover:bg-gray-200 transition-colors cursor-pointer" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Modifier matière:', s);
                  setEditing(s);
                  setFormData(s);
                  setShowModal(true);
                }}
                title="Modifier"
              >
                <Edit className="w-4 h-4 text-gray-600" />
              </button>
              <button 
                type="button"
                className="p-1.5 rounded hover:bg-red-50 transition-colors cursor-pointer" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete(s.id);
                }}
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4 text-red-600" />
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-md shadow-lg w-full max-w-md p-4">
            <h4 className="font-semibold mb-4">{editing ? 'Modifier' : 'Nouvelle'} matière</h4>
            <div className="space-y-3">
              <div>
                <label className="label">Nom</label>
                <input className="input w-full" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div>
                <label className="label">Couleur</label>
                <input type="color" className="w-full h-10 rounded border" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} />
              </div>
              <div>
                <label className="label">Durée par défaut (h)</label>
                <input type="number" className="input w-full" value={formData.default_duration} onChange={(e) => setFormData({ ...formData, default_duration: parseInt(e.target.value) || 1 })} />
              </div>
              <div className="flex justify-end gap-2">
                <button className="btn" onClick={() => setShowModal(false)}>Annuler</button>
                <button className="btn btn-primary" onClick={handleSave}>Valider</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant CRUD pour les salles
const RoomsManager = ({ rooms, onRefresh }) => {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', capacity: '', type: 'classique' });

  // Dédupliquer les salles par nom (garder le premier trouvé)
  const uniqueRooms = React.useMemo(() => {
    const seen = new Map();
    return (rooms || []).filter(room => {
      const key = (room.name || '').toLowerCase().trim();
      if (!key || seen.has(key)) {
        return false;
      }
      seen.set(key, true);
      return true;
    });
  }, [rooms]);

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`/api/edt/rooms/${editing.id}`, formData);
        toast.success('Salle mise à jour');
      } else {
        await axios.post('/api/edt/rooms', formData);
        toast.success('Salle créée');
      }
      setShowModal(false);
      setEditing(null);
      setFormData({ name: '', capacity: '', type: 'classique' });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    console.log('Suppression salle, ID:', id);
    if (!window.confirm('Supprimer cette salle ?')) return;
    try {
      const response = await axios.delete(`/api/edt/rooms/${id}`);
      console.log('Réponse suppression:', response.data);
      toast.success('Salle supprimée');
      onRefresh();
    } catch (error) {
      console.error('Erreur suppression salle:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">Salles</h3>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setFormData({ name: '', capacity: '', type: 'classique' }); setShowModal(true); }}>
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>
      <div className="space-y-2">
        {uniqueRooms.map(r => (
          <div key={r.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors">
            <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></div>
            <span className="text-sm font-medium text-gray-800 flex-1 min-w-0 break-words pr-2">
              {r.name}
              {r.type && <span className="text-gray-500 ml-2">({r.type})</span>}
            </span>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button 
                type="button"
                className="p-1.5 rounded hover:bg-gray-200 transition-colors cursor-pointer" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Modifier salle:', r);
                  setEditing(r);
                  setFormData(r);
                  setShowModal(true);
                }}
                title="Modifier"
              >
                <Edit className="w-4 h-4 text-gray-600" />
              </button>
              <button 
                type="button"
                className="p-1.5 rounded hover:bg-red-50 transition-colors cursor-pointer" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete(r.id);
                }}
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4 text-red-600" />
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-md shadow-lg w-full max-w-md p-4">
            <h4 className="font-semibold mb-4">{editing ? 'Modifier' : 'Nouvelle'} salle</h4>
            <div className="space-y-3">
              <div>
                <label className="label">Nom</label>
                <input className="input w-full" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div>
                <label className="label">Capacité</label>
                <input type="number" className="input w-full" value={formData.capacity} onChange={(e) => setFormData({ ...formData, capacity: e.target.value })} />
              </div>
              <div>
                <label className="label">Type</label>
                <select className="input w-full" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                  <option value="classique">Classique</option>
                  <option value="atelier">Atelier</option>
                  <option value="info">Info</option>
                  <option value="laboratoire">Laboratoire</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button className="btn" onClick={() => setShowModal(false)}>Annuler</button>
                <button className="btn btn-primary" onClick={handleSave}>Valider</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant CRUD pour les créneaux horaires
const TimeslotsManager = ({ timeslots, onRefresh }) => {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ start_time: '', end_time: '', label: '', order_index: 0 });

  const handleSave = async () => {
    if (!formData.start_time || !formData.end_time || !formData.label) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      const dataToSend = {
        start_time: formData.start_time,
        end_time: formData.end_time,
        label: formData.label,
        order_index: formData.order_index || 0
      };
      
      if (editing) {
        await axios.put(`/api/edt/timeslots/${editing.id}`, dataToSend);
        toast.success('Créneau mis à jour');
      } else {
        await axios.post('/api/edt/timeslots', dataToSend);
        toast.success('Créneau créé');
      }
      setShowModal(false);
      setEditing(null);
      setFormData({ start_time: '', end_time: '', label: '', order_index: 0 });
      onRefresh();
    } catch (error) {
      console.error('Erreur sauvegarde créneau:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce créneau ?')) return;
    try {
      await axios.delete(`/api/edt/timeslots/${id}`);
      toast.success('Créneau supprimé');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">Créneaux horaires</h3>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setFormData({ start_time: '', end_time: '', label: '', order_index: timeslots.length }); setShowModal(true); }}>
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>
      <div className="space-y-2">
        {timeslots.map(t => (
          <div key={t.id} className="flex items-center justify-between p-2 border rounded">
            <span className="text-sm">{t.label} ({t.start_time} - {t.end_time})</span>
            <div className="flex gap-1">
              <button className="btn btn-sm" onClick={() => { setEditing(t); setFormData(t); setShowModal(true); }}>
                <Edit className="w-4 h-4" />
              </button>
              <button className="btn btn-sm" onClick={() => handleDelete(t.id)}>
                <Trash2 className="w-4 h-4 text-red-600" />
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-md shadow-lg w-full max-w-md p-4">
            <h4 className="font-semibold mb-4">{editing ? 'Modifier' : 'Nouveau'} créneau</h4>
            <div className="space-y-3">
              <div>
                <label className="label">Heure début (HH:MM)</label>
                <input type="time" className="input w-full" value={formData.start_time} onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} />
              </div>
              <div>
                <label className="label">Heure fin (HH:MM)</label>
                <input type="time" className="input w-full" value={formData.end_time} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} />
              </div>
              <div>
                <label className="label">Libellé</label>
                <input className="input w-full" value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} placeholder="ex: 8h-9h" />
              </div>
              <div>
                <label className="label">Ordre</label>
                <input type="number" className="input w-full" value={formData.order_index} onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="flex justify-end gap-2">
                <button className="btn" onClick={() => setShowModal(false)}>Annuler</button>
                <button className="btn btn-primary" onClick={handleSave}>Valider</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant principal de la grille EDT
const EDTGrid = ({ sessions, timeslots, classes, subjects, teachers, rooms, onCellClick, onSessionDragStart, onSessionDrop, draggedSession, selectedClassId, currentWeek = 0, onResizeSession }) => {
  const [resizingSession, setResizingSession] = useState(null);
  // Calculer les dates de la semaine (lundi à vendredi)
  const getWeekDates = () => {
    const today = new Date();
    const day = today.getDay(); // 0 = dimanche, 1 = lundi, etc.
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Ajuster pour lundi
    const monday = new Date(today);
    monday.setDate(today.getDate() - diff + 1);
    // Ajouter le nombre de semaines (currentWeek)
    monday.setDate(monday.getDate() + (currentWeek * 7));
    
    const days = [];
    const dayNames = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'];
    
    for (let i = 0; i < 5; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      days.push({
        id: i + 1,
        name: dayNames[i],
        date: date,
        dateStr: `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`
      });
    }
    
    return days;
  };

  const days = getWeekDates();

  const getSessionForCell = (dayId, timeslotId) => {
    return sessions.find(s => 
      s.day_of_week === dayId && 
      s.timeslot_id === timeslotId
    );
  };

  // Fonction pour détecter les séances consécutives et calculer leur étendue
  const getSessionSpan = (dayId, timeslotId) => {
    const session = getSessionForCell(dayId, timeslotId);
    if (!session) return { session: null, span: 1, isStart: true, shouldRender: true };

    let currentTimeslotIndex = timeslots.findIndex(t => t.id === timeslotId);
    if (currentTimeslotIndex === -1) return { session: null, span: 1, isStart: true, shouldRender: true };

    // Vérifier si cette cellule est le début du bloc
    const isStart = currentTimeslotIndex === 0 || 
      (currentTimeslotIndex > 0 && (() => {
        const prevSession = getSessionForCell(dayId, timeslots[currentTimeslotIndex - 1].id);
        return !prevSession || 
               String(prevSession.class_id) !== String(session.class_id) ||
               String(prevSession.teacher_id) !== String(session.teacher_id) ||
               String(prevSession.subject_id) !== String(session.subject_id) ||
               String(prevSession.room_id || '') !== String(session.room_id || '');
      })());

    if (!isStart) {
      // Ce n'est pas le début, ne pas rendre cette cellule
      return { session: null, span: 1, isStart: false, shouldRender: false };
    }

    // Trouver toutes les séances consécutives (même jour, même classe, même professeur, même matière, créneaux qui se suivent)
    let span = 1;
    
    // Si cette session est en cours de redimensionnement, utiliser le span de prévisualisation
    if (resizingSession && resizingSession.sessionId === session.id && resizingSession.dayId === dayId) {
      span = resizingSession.previewSpan;
    } else {
      for (let i = currentTimeslotIndex + 1; i < timeslots.length; i++) {
        const nextSession = getSessionForCell(dayId, timeslots[i].id);
        if (nextSession && 
            String(nextSession.class_id) === String(session.class_id) &&
            String(nextSession.teacher_id) === String(session.teacher_id) &&
            String(nextSession.subject_id) === String(session.subject_id) &&
            String(nextSession.room_id || '') === String(session.room_id || '')) {
          span++;
        } else {
          break;
        }
      }
    }

    return {
      session: session,
      span: span,
      isStart: true,
      shouldRender: true
    };
  };

  // Fonction pour déterminer si une ligne de séparation doit être affichée
  const shouldShowSeparator = (timeslot, index) => {
    const time = timeslot.start_time || '';
    // Lignes jaunes à 09h30 et 15h40
    if (time === '09:30' || time === '15:40') {
      console.log('Ligne jaune détectée pour:', time, 'timeslot:', timeslot);
      return 'yellow';
    }
    // Lignes vertes à 11h30 et 13h30
    if (time === '11:30' || time === '13:30') {
      console.log('Ligne verte détectée pour:', time, 'timeslot:', timeslot);
      return 'green';
    }
    return null;
  };

  // Fonction pour formater l'heure (07h20 au lieu de 07:20)
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.replace(':', 'h');
  };

  // Fonction pour formater l'intervalle complet (7h20-8h20)
  const formatTimeSlot = (timeslot) => {
    const start = formatTime(timeslot.start_time);
    const end = formatTime(timeslot.end_time);
    if (start && end) {
      return `${start}-${end}`;
    }
    // Fallback sur le label si les heures ne sont pas disponibles
    return timeslot.label || start || '';
  };

  return (
    <div className="overflow-auto border border-black rounded-lg bg-gray-100">
      <table className="min-w-full border-collapse bg-gray-100" style={{ borderCollapse: 'collapse' }}>
        <thead className="bg-gray-200 sticky top-0 z-10">
          <tr>
            <th className="border border-black px-4 py-3 text-sm font-semibold text-gray-800 sticky left-0 bg-gray-100 z-20 min-w-[120px] border-r border-black">
              Créneaux
            </th>
            {days.map(day => (
              <th key={day.id} className="border border-black px-4 py-3 text-sm font-semibold text-gray-800 min-w-[180px] text-center border-l border-r border-black">
                <div className="font-bold">{day.name}</div>
                <div className="text-xs font-normal text-gray-600 mt-1">{day.dateStr}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {timeslots.map((timeslot, index) => {
            const separator = shouldShowSeparator(timeslot, index);
            const isYellow = separator === 'yellow';
            const isGreen = separator === 'green';
            const borderTopClass = isYellow 
              ? 'border-t-2 border-yellow-500' 
              : isGreen
              ? 'border-t-2 border-green-600'
              : 'border-t border-black';
            
            // Style inline pour forcer l'affichage
            const borderTopStyle = isYellow 
              ? { borderTop: '2px solid #eab308' } 
              : isGreen
              ? { borderTop: '2px solid #16a34a' }
              : {};
            
            // Clé unique basée sur l'ID et l'intervalle de temps pour éviter les doublons React
            const uniqueKey = `timeslot-${timeslot.id}-${timeslot.start_time}-${timeslot.end_time}`;
            
            return (
              <tr key={uniqueKey} style={borderTopStyle} className={borderTopClass}>
                 <td className="border-r border-black border-b border-black px-3 py-2 text-sm font-medium text-gray-800 sticky left-0 bg-gray-100 z-10 h-[60px] relative" style={{...borderTopStyle, padding: '8px 12px'}}>
                  <div className="absolute top-1 left-3">{formatTime(timeslot.start_time)}</div>
                  <div className="absolute bottom-1 left-3">{formatTime(timeslot.end_time)}</div>
                </td>
                {days.map(day => {
                  const sessionInfo = getSessionSpan(day.id, timeslot.id);
                  const session = sessionInfo.session;
                  const span = sessionInfo.span;
                  const shouldRender = sessionInfo.shouldRender;
                  const isDragOver = draggedSession && draggedSession.targetDay === day.id && draggedSession.targetTimeslot === timeslot.id;
                  
                  // Si ce n'est pas le début du bloc, ne pas afficher la cellule (elle est fusionnée)
                  if (!shouldRender) {
                    return null;
                  }
                  
                  return (
                     <td
                       key={day.id}
                       rowSpan={session ? span : 1}
                       className={`border-l border-r border-b border-black relative ${
                         session 
                           ? 'bg-transparent' 
                           : 'bg-gray-100'
                       } ${
                         isDragOver ? 'bg-blue-200' : session ? '' : 'hover:bg-gray-200'
                       }`}
                       style={{
                         ...borderTopStyle,
                         padding: 0,
                         margin: 0,
                         verticalAlign: 'top',
                         height: session && span > 1 ? `${span * 60}px` : '60px',
                         lineHeight: 0
                       }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (draggedSession) {
                          draggedSession.targetDay = day.id;
                          draggedSession.targetTimeslot = timeslot.id;
                          // Feedback visuel : mettre en évidence la cellule cible
                          e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.2)';
                        }
                      }}
                      onDragLeave={(e) => {
                        // Réinitialiser la couleur de fond
                        e.currentTarget.style.backgroundColor = '';
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.style.backgroundColor = '';
                        if (draggedSession && draggedSession.session) {
                          // S'assurer que la cellule cible est bien définie
                          const targetDay = draggedSession.targetDay || day.id;
                          const targetTimeslot = draggedSession.targetTimeslot || timeslot.id;
                          onSessionDrop(draggedSession.session, targetDay, targetTimeslot);
                        }
                      }}
                      onClick={() => !session && onCellClick(day.id, timeslot.id)}
                    >
                      {session ? (
                        <div
                          className="text-xs cursor-move relative group"
                          style={{ 
                            backgroundColor: session.color || session.subject_color || '#3B82F6',
                            color: 'white',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: span > 1 ? 'space-evenly' : 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            padding: span > 1 ? `${Math.max(12, span * 4)}px 8px` : '8px',
                            boxSizing: 'border-box',
                            margin: 0,
                            border: 'none',
                            height: '100%',
                            width: '100%',
                            minHeight: span > 1 ? `${span * 60}px` : '60px'
                          }}
                          draggable
                          onDragStart={(e) => onSessionDragStart(e, session)}
                          onMouseDown={(e) => {
                            // Ne pas déclencher le drag si on clique sur les poignées
                            if (e.target.closest('.resize-handle')) {
                              e.stopPropagation();
                              return;
                            }
                          }}
                          onClick={(e) => {
                            // Ne pas ouvrir le modal si on clique sur les poignées
                            if (e.target.closest('.resize-handle')) {
                              e.stopPropagation();
                              return;
                            }
                            e.stopPropagation();
                            onCellClick(day.id, timeslot.id, session);
                          }}
                        >
                          <div 
                            className="font-bold leading-tight"
                            style={{ 
                              fontSize: span > 1 ? '14px' : '13px',
                              marginBottom: span > 1 ? '4px' : '6px'
                            }}
                          >
                            {session.subject_name}
                          </div>
                          <div 
                            className="opacity-95 leading-tight"
                            style={{ 
                              fontSize: span > 1 ? '12px' : '11px',
                              marginBottom: span > 1 ? '4px' : '4px'
                            }}
                          >
                            {session.teacher_first_name && session.teacher_last_name 
                              ? `${session.teacher_first_name[0]}.${session.teacher_last_name}` 
                              : 'Prof'}
                          </div>
                          {session.room_name && (
                            <div 
                              className="opacity-95 leading-tight"
                              style={{ fontSize: span > 1 ? '12px' : '11px' }}
                            >
                              {session.room_name}
                            </div>
                          )}
                          {/* Poignée de redimensionnement en bas - plus grande et visible */}
                          <div 
                            className="resize-handle absolute bottom-0 left-0 right-0 h-6 cursor-ns-resize opacity-70 group-hover:opacity-100 transition-opacity bg-white/40 hover:bg-white/60 flex items-center justify-center z-10"
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              
                              const currentTimeslotIndex = timeslots.findIndex(t => t.id === timeslot.id);
                              const blockSessions = [session];
                              for (let i = currentTimeslotIndex + 1; i < timeslots.length; i++) {
                                const nextSession = sessions.find(s => 
                                  s.day_of_week === day.id && 
                                  s.timeslot_id === timeslots[i].id &&
                                  s.class_id === session.class_id &&
                                  s.teacher_id === session.teacher_id &&
                                  s.subject_id === session.subject_id &&
                                  s.room_id === session.room_id
                                );
                                if (nextSession) {
                                  blockSessions.push(nextSession);
                                } else {
                                  break;
                                }
                              }
                              
                              const startY = e.clientY;
                              const initialSpan = blockSessions.length;
                              let currentPreviewSpan = initialSpan;
                              
                              // Empêcher la sélection de texte pendant le drag
                              document.body.style.userSelect = 'none';
                              document.body.style.cursor = 'ns-resize';
                              
                              setResizingSession({
                                sessionId: session.id,
                                dayId: day.id,
                                timeslotId: timeslot.id,
                                direction: 'extend',
                                startY: startY,
                                initialSpan: initialSpan,
                                previewSpan: initialSpan
                              });
                              
                              const handleMouseMove = (moveEvent) => {
                                moveEvent.preventDefault();
                                const deltaY = moveEvent.clientY - startY;
                                const cellHeight = 60;
                                const additionalSlots = Math.max(0, Math.floor(deltaY / cellHeight));
                                const maxSpan = timeslots.length - currentTimeslotIndex;
                                const newSpan = Math.min(initialSpan + additionalSlots, maxSpan);
                                
                                if (newSpan !== currentPreviewSpan) {
                                  currentPreviewSpan = newSpan;
                                  setResizingSession(prev => prev ? {
                                    ...prev,
                                    previewSpan: newSpan
                                  } : null);
                                }
                              };
                              
                              const handleMouseUp = async () => {
                                document.body.style.userSelect = '';
                                document.body.style.cursor = '';
                                
                                const finalSpan = currentPreviewSpan;
                                const additionalSlots = finalSpan - initialSpan;
                                
                                setResizingSession(null);
                                document.removeEventListener('mousemove', handleMouseMove);
                                document.removeEventListener('mouseup', handleMouseUp);
                                
                                if (additionalSlots > 0 && onResizeSession) {
                                  // Créer les séances supplémentaires une par une
                                  for (let i = 0; i < additionalSlots; i++) {
                                    const targetTimeslotIndex = currentTimeslotIndex + initialSpan + i;
                                    if (targetTimeslotIndex < timeslots.length) {
                                      await onResizeSession(session, day.id, timeslot.id, 'extend');
                                      // Attendre un peu pour que la séance soit créée avant de continuer
                                      await new Promise(resolve => setTimeout(resolve, 50));
                                    }
                                  }
                                }
                              };
                              
                              document.addEventListener('mousemove', handleMouseMove, { passive: false });
                              document.addEventListener('mouseup', handleMouseUp);
                            }}
                            title="Glisser pour étendre vers le bas"
                          >
                            <div className="w-12 h-1.5 bg-white/90 rounded shadow-sm pointer-events-none"></div>
                          </div>
                          {/* Poignée de redimensionnement en haut */}
                          <div 
                            className="resize-handle absolute top-0 left-0 right-0 h-6 cursor-ns-resize opacity-70 group-hover:opacity-100 transition-opacity bg-white/40 hover:bg-white/60 flex items-center justify-center z-10"
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              
                              const currentTimeslotIndex = timeslots.findIndex(t => t.id === timeslot.id);
                              const blockSessions = [session];
                              for (let i = currentTimeslotIndex + 1; i < timeslots.length; i++) {
                                const nextSession = sessions.find(s => 
                                  s.day_of_week === day.id && 
                                  s.timeslot_id === timeslots[i].id &&
                                  s.class_id === session.class_id &&
                                  s.teacher_id === session.teacher_id &&
                                  s.subject_id === session.subject_id &&
                                  s.room_id === session.room_id
                                );
                                if (nextSession) {
                                  blockSessions.push(nextSession);
                                } else {
                                  break;
                                }
                              }
                              
                              const startY = e.clientY;
                              const initialSpan = blockSessions.length;
                              let currentPreviewSpan = initialSpan;
                              
                              // Empêcher la sélection de texte pendant le drag
                              document.body.style.userSelect = 'none';
                              document.body.style.cursor = 'ns-resize';
                              
                              setResizingSession({
                                sessionId: session.id,
                                dayId: day.id,
                                timeslotId: timeslot.id,
                                direction: 'shrink',
                                startY: startY,
                                initialSpan: initialSpan,
                                previewSpan: initialSpan
                              });
                              
                              const handleMouseMove = (moveEvent) => {
                                moveEvent.preventDefault();
                                const deltaY = startY - moveEvent.clientY; // Inversé pour shrink
                                const cellHeight = 60;
                                const removedSlots = Math.max(0, Math.floor(deltaY / cellHeight));
                                const newSpan = Math.max(1, initialSpan - removedSlots);
                                
                                if (newSpan !== currentPreviewSpan) {
                                  currentPreviewSpan = newSpan;
                                  setResizingSession(prev => prev ? {
                                    ...prev,
                                    previewSpan: newSpan
                                  } : null);
                                }
                              };
                              
                              const handleMouseUp = async () => {
                                document.body.style.userSelect = '';
                                document.body.style.cursor = '';
                                
                                const finalSpan = currentPreviewSpan;
                                const removedSlots = initialSpan - finalSpan;
                                
                                setResizingSession(null);
                                document.removeEventListener('mousemove', handleMouseMove);
                                document.removeEventListener('mouseup', handleMouseUp);
                                
                                if (removedSlots > 0 && onResizeSession) {
                                  // Supprimer les séances une par une
                                  for (let i = 0; i < removedSlots; i++) {
                                    await onResizeSession(session, day.id, timeslot.id, 'shrink');
                                    // Attendre un peu pour que la séance soit supprimée avant de continuer
                                    await new Promise(resolve => setTimeout(resolve, 50));
                                  }
                                }
                              };
                              
                              document.addEventListener('mousemove', handleMouseMove, { passive: false });
                              document.addEventListener('mouseup', handleMouseUp);
                            }}
                            title="Glisser pour réduire vers le haut"
                          >
                            <div className="w-12 h-1.5 bg-white/90 rounded shadow-sm pointer-events-none"></div>
                          </div>
                        </div>
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-gray-500 text-[11px] cursor-pointer hover:bg-gray-200 transition-colors" style={{ margin: 0, padding: 0, minHeight: '60px' }}>
                          <span className="opacity-70">Cliquer pour ajouter</span>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Composant principal
const EmploisDuTemps = () => {
  const { selectedYear } = useSchoolYear();
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [timeslots, setTimeslots] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [viewMode, setViewMode] = useState('class'); // 'class', 'teacher', 'room'
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [modalDay, setModalDay] = useState(null);
  const [modalTimeslot, setModalTimeslot] = useState(null);
  const [draggedSession, setDraggedSession] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(0); // 0 = semaine actuelle, -1 = semaine précédente, +1 = semaine suivante

  // Charger les données
  useEffect(() => {
    loadAllData();
  }, [selectedYear]);

  useEffect(() => {
    loadSessions();
  }, [selectedClassId, selectedTeacherId, selectedRoomId, viewMode, selectedYear]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const results = await Promise.allSettled([
        axios.get(`/api/classes?school_year=${selectedYear}`),
        axios.get('/api/edt/subjects'),
        axios.get('/api/users'),
        axios.get('/api/edt/rooms'),
        axios.get('/api/edt/timeslots')
      ]);

      // Traiter chaque résultat
      if (results[0].status === 'fulfilled' && results[0].value.data?.success) {
        setClasses(results[0].value.data.data || []);
        if (results[0].value.data.data?.length > 0) {
          setSelectedClassId(String(results[0].value.data.data[0].id));
        }
      }
      
      if (results[1].status === 'fulfilled' && results[1].value.data?.success) {
        const subjectsData = results[1].value.data.data || [];
        // Dédupliquer les matières par nom
        const uniqueSubjectsMap = new Map();
        subjectsData.forEach(subject => {
          const key = (subject.name || '').toLowerCase().trim();
          if (key && !uniqueSubjectsMap.has(key)) {
            uniqueSubjectsMap.set(key, subject);
          }
        });
        const uniqueSubjects = Array.from(uniqueSubjectsMap.values());
        setSubjects(uniqueSubjects);
        if (subjectsData.length !== uniqueSubjects.length) {
          console.log(`Matières dédupliquées: ${subjectsData.length} -> ${uniqueSubjects.length}`);
        }
      } else if (results[1].status === 'rejected') {
        console.error('Erreur chargement matières:', results[1].reason);
      }
      
      if (results[2].status === 'fulfilled' && results[2].value.data?.success) {
        // Filtrer uniquement les utilisateurs avec le rôle "teacher" (comme dans la page Utilisateurs)
        const allUsers = results[2].value.data.data || [];
        const teachersOnly = allUsers.filter(user => 
          user.role === 'teacher' || 
          user.role === 'Teacher' ||
          user.role === 'professeur' ||
          user.role === 'Professeur'
        );
        setTeachers(teachersOnly);
      } else if (results[2].status === 'rejected') {
        console.error('Erreur chargement professeurs:', results[2].reason);
        // Ne pas bloquer l'application si les professeurs ne peuvent pas être chargés
        setTeachers([]);
      }
      
      if (results[3].status === 'fulfilled' && results[3].value.data?.success) {
        const roomsData = results[3].value.data.data || [];
        // Dédupliquer les salles par nom
        const uniqueRoomsMap = new Map();
        roomsData.forEach(room => {
          const key = (room.name || '').toLowerCase().trim();
          if (key && !uniqueRoomsMap.has(key)) {
            uniqueRoomsMap.set(key, room);
          }
        });
        const uniqueRooms = Array.from(uniqueRoomsMap.values());
        setRooms(uniqueRooms);
        if (roomsData.length !== uniqueRooms.length) {
          console.log(`Salles dédupliquées: ${roomsData.length} -> ${uniqueRooms.length}`);
        }
      } else if (results[3].status === 'rejected') {
        console.error('Erreur chargement salles:', results[3].reason);
      }
      
      if (results[4].status === 'fulfilled' && results[4].value.data?.success) {
        const timeslotsData = results[4].value.data.data || [];
        
        // Dédupliquer les créneaux par start_time et end_time (garder le premier trouvé)
        const uniqueTimeslotsMap = new Map();
        
        timeslotsData.forEach(timeslot => {
          const key = `${timeslot.start_time || ''}-${timeslot.end_time || ''}`;
          if (!uniqueTimeslotsMap.has(key)) {
            uniqueTimeslotsMap.set(key, timeslot);
          }
        });
        
        // Convertir la Map en tableau
        const uniqueTimeslots = Array.from(uniqueTimeslotsMap.values());
        
        // Trier les créneaux par order_index puis par start_time
        const sortedTimeslots = uniqueTimeslots.sort((a, b) => {
          if (a.order_index !== b.order_index) {
            return (a.order_index || 0) - (b.order_index || 0);
          }
          return (a.start_time || '').localeCompare(b.start_time || '');
        });
        
        // Limiter à 10 créneaux maximum (garder les 10 premiers après tri)
        const limitedTimeslots = sortedTimeslots.slice(0, 10);
        
        setTimeslots(limitedTimeslots);
        console.log('Créneaux chargés (dédupliqués et limités):', limitedTimeslots.length, 'sur', timeslotsData.length, 'créneaux initiaux');
        console.log('Créneaux:', limitedTimeslots.map(t => `${t.start_time}-${t.end_time}`));
        
        if (sortedTimeslots.length > 10) {
          console.warn(`Attention: ${sortedTimeslots.length} créneaux uniques trouvés, limité à 10`);
        }
      } else if (results[4].status === 'rejected') {
        console.error('Erreur chargement créneaux:', results[4].reason);
        setTimeslots([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    try {
      // Toujours charger toutes les séances, puis filtrer côté client selon le mode de vue
      // Cela permet d'avoir toutes les données disponibles pour la synchronisation entre les vues
      const res = await axios.get('/api/edt/sessions');
      if (res.data?.success) {
        let sessionsData = res.data.data || [];
        
        // Filtrer selon le mode de vue
        if (viewMode === 'class' && selectedClassId) {
          sessionsData = sessionsData.filter(s => String(s.class_id) === String(selectedClassId));
        } else if (viewMode === 'teacher' && selectedTeacherId) {
          sessionsData = sessionsData.filter(s => String(s.teacher_id) === String(selectedTeacherId));
        } else if (viewMode === 'room' && selectedRoomId) {
          sessionsData = sessionsData.filter(s => String(s.room_id) === String(selectedRoomId));
        }
        
        setSessions(sessionsData);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des séances:', error);
    }
  };

  const handleCellClick = (dayOfWeek, timeslotId, session = null) => {
    setModalDay(dayOfWeek);
    setModalTimeslot(timeslotId);
    setEditingSession(session);
    setShowSessionModal(true);
  };

  const handleSaveSession = async (formData) => {
    try {
      if (editingSession) {
        // En mode édition, on modifie une seule séance
        await axios.put(`/api/edt/sessions/${editingSession.id}`, formData);
        toast.success('Séance mise à jour');
      } else {
        // En mode création, créer plusieurs séances si une plage est sélectionnée
        const startId = formData.timeslot_start_id || formData.timeslot_id;
        const endId = formData.timeslot_end_id || formData.timeslot_start_id || formData.timeslot_id;
        
        if (startId && endId && timeslots.length > 0) {
          const startIndex = timeslots.findIndex(t => t.id === parseInt(startId));
          const endIndex = timeslots.findIndex(t => t.id === parseInt(endId));
          
          if (startIndex === -1 || endIndex === -1) {
            toast.error('Créneaux invalides');
            return;
          }
          
          if (endIndex < startIndex) {
            toast.error('Le créneau de fin doit être après le créneau de début');
            return;
          }
          
          // Créer une séance pour chaque créneau entre début et fin
          const span = endIndex - startIndex + 1;
          const promises = [];
          
          for (let i = startIndex; i <= endIndex; i++) {
            const targetTimeslot = timeslots[i];
            const sessionData = {
              ...formData,
              timeslot_id: targetTimeslot.id,
              day_of_week: formData.day_of_week
            };
            // Ne pas inclure timeslot_start_id et timeslot_end_id dans les données de session
            delete sessionData.timeslot_start_id;
            delete sessionData.timeslot_end_id;
            promises.push(axios.post('/api/edt/sessions', sessionData));
          }
          
          await Promise.all(promises);
          toast.success(`${span} séance${span > 1 ? 's' : ''} créée${span > 1 ? 's' : ''}`);
        } else {
          // Créer une seule séance
          const sessionData = { ...formData };
          delete sessionData.timeslot_start_id;
          delete sessionData.timeslot_end_id;
          await axios.post('/api/edt/sessions', sessionData);
          toast.success('Séance créée');
        }
      }
      setShowSessionModal(false);
      setEditingSession(null);
      loadSessions();
    } catch (error) {
      if (error.response?.status === 409) {
        const conflicts = error.response.data.conflicts || [];
        toast.error(`Conflit détecté: ${conflicts.map(c => c.message).join(', ')}`);
      } else {
        toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
      }
    }
  };

  const handleDeleteSession = async (id) => {
    if (!window.confirm('Supprimer cette séance ?')) return;
    try {
      await axios.delete(`/api/edt/sessions/${id}`);
      toast.success('Séance supprimée');
      loadSessions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleSessionDragStart = (e, session) => {
    setDraggedSession({ session, targetDay: null, targetTimeslot: null });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSessionDrop = async (session, newDay, newTimeslot) => {
    try {
      // Vérifier que le créneau cible existe
      const targetTimeslot = timeslots.find(t => t.id === newTimeslot);
      if (!targetTimeslot) {
        toast.error('Créneau invalide');
        setDraggedSession(null);
        return;
      }

      // Si on déplace une séance qui fait partie d'un bloc, déplacer tout le bloc
      const currentTimeslotIndex = timeslots.findIndex(t => t.id === session.timeslot_id);
      const targetTimeslotIndex = timeslots.findIndex(t => t.id === newTimeslot);
      
      if (currentTimeslotIndex === -1 || targetTimeslotIndex === -1) {
        toast.error('Créneau invalide');
        setDraggedSession(null);
        return;
      }

      // Trouver toutes les séances consécutives du bloc
      const blockSessions = [session];
      for (let i = currentTimeslotIndex + 1; i < timeslots.length; i++) {
        const nextSession = sessions.find(s => 
          s.day_of_week === session.day_of_week && 
          s.timeslot_id === timeslots[i].id &&
          s.class_id === session.class_id &&
          s.teacher_id === session.teacher_id &&
          s.subject_id === session.subject_id &&
          s.room_id === session.room_id
        );
        if (nextSession) {
          blockSessions.push(nextSession);
        } else {
          break;
        }
      }

      // Vérifier que tous les créneaux cibles sont libres
      const blockSize = blockSessions.length;
      if (targetTimeslotIndex + blockSize > timeslots.length) {
        toast.error('Pas assez de créneaux disponibles');
        setDraggedSession(null);
        return;
      }

      // Vérifier les conflits
      for (let i = 0; i < blockSize; i++) {
        const targetSlot = timeslots[targetTimeslotIndex + i];
        const existingSession = sessions.find(s => 
          s.day_of_week === newDay && 
          s.timeslot_id === targetSlot.id &&
          s.id !== blockSessions[i]?.id
        );
        if (existingSession) {
          toast.error(`Conflit : le créneau ${targetSlot.start_time}-${targetSlot.end_time} est déjà occupé`);
          setDraggedSession(null);
          return;
        }
      }

      // Déplacer toutes les séances du bloc
      const movePromises = blockSessions.map((s, index) => {
        const targetSlot = timeslots[targetTimeslotIndex + index];
        return axios.put(`/api/edt/sessions/${s.id}`, {
          ...s,
          day_of_week: newDay,
          timeslot_id: targetSlot.id
        });
      });

      await Promise.all(movePromises);
      toast.success(`Bloc de ${blockSize} créneau${blockSize > 1 ? 'x' : ''} déplacé`);
      setDraggedSession(null);
      loadSessions();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('Conflit détecté : impossible de déplacer la séance');
      } else {
        toast.error('Erreur lors du déplacement');
      }
      setDraggedSession(null);
    }
  };

  const handleResizeSession = async (session, dayId, timeslotId, direction) => {
    try {
      console.log('handleResizeSession appelé:', { session, dayId, timeslotId, direction });
      
      const currentTimeslotIndex = timeslots.findIndex(t => t.id === timeslotId);
      if (currentTimeslotIndex === -1) {
        console.error('Créneau invalide:', timeslotId);
        toast.error('Créneau invalide');
        return;
      }

      // Trouver toutes les séances consécutives du bloc actuel
      const blockSessions = [session];
      for (let i = currentTimeslotIndex + 1; i < timeslots.length; i++) {
        const nextSession = sessions.find(s => 
          s.day_of_week === dayId && 
          s.timeslot_id === timeslots[i].id &&
          s.class_id === session.class_id &&
          s.teacher_id === session.teacher_id &&
          s.subject_id === session.subject_id &&
          s.room_id === session.room_id
        );
        if (nextSession) {
          blockSessions.push(nextSession);
        } else {
          break;
        }
      }

      // Déterminer la couleur à utiliser (celle de la première séance du bloc)
      const baseColor = session.color || session.subject_color || '#3B82F6';

      if (direction === 'extend') {
        // Étendre vers le bas : créer une nouvelle séance dans le créneau suivant
        const lastTimeslotIndex = currentTimeslotIndex + blockSessions.length - 1;
        console.log('Dernier index du bloc:', lastTimeslotIndex, 'sur', timeslots.length - 1);
        
        if (lastTimeslotIndex < timeslots.length - 1) {
          const nextTimeslot = timeslots[lastTimeslotIndex + 1];
          console.log('Créneau suivant:', nextTimeslot);
          
          // Vérifier si le créneau suivant est libre
          const existingSession = sessions.find(s => 
            s.day_of_week === dayId && 
            s.timeslot_id === nextTimeslot.id
          );
          
          if (!existingSession) {
            // Créer une nouvelle séance identique dans le créneau suivant
            // Utiliser la même couleur que celle de la première séance du bloc
            const newSessionData = {
              class_id: session.class_id,
              subject_id: session.subject_id,
              teacher_id: session.teacher_id,
              room_id: session.room_id,
              day_of_week: dayId,
              timeslot_id: nextTimeslot.id,
              notes: session.notes || '',
              color: baseColor
            };
            console.log('Création nouvelle séance:', newSessionData);
            try {
              await axios.post('/api/edt/sessions', newSessionData);
              toast.success(`Bloc étendu : ${nextTimeslot.start_time}-${nextTimeslot.end_time}`);
              loadSessions();
            } catch (error) {
              console.error('Erreur création séance:', error);
              toast.error(error.response?.data?.message || 'Erreur lors de l\'extension');
            }
          } else {
            toast.error(`Le créneau ${nextTimeslot.start_time}-${nextTimeslot.end_time} est déjà occupé`);
          }
        } else {
          toast.info('Dernier créneau atteint');
        }
      } else if (direction === 'shrink') {
        // Rétrécir vers le haut : supprimer la dernière séance du bloc
        console.log('Réduction du bloc, taille actuelle:', blockSessions.length);
        if (blockSessions.length > 1) {
          // Supprimer la dernière séance
          const lastSession = blockSessions[blockSessions.length - 1];
          const lastTimeslot = timeslots.find(t => t.id === lastSession.timeslot_id);
          console.log('Suppression séance:', lastSession.id);
          await axios.delete(`/api/edt/sessions/${lastSession.id}`);
          toast.success(`Bloc réduit : ${lastTimeslot?.start_time}-${lastTimeslot?.end_time} supprimé`);
          loadSessions();
        } else {
          toast.info('La séance ne peut pas être réduite davantage (1 créneau minimum)');
        }
      }
    } catch (error) {
      console.error('Erreur lors du redimensionnement:', error);
      if (error.response?.status === 409) {
        toast.error('Conflit détecté lors du redimensionnement');
      } else {
        toast.error(error.response?.data?.message || 'Erreur lors du redimensionnement');
      }
    }
  };

  const handleExportJSON = () => {
    const data = { classes, subjects, teachers, rooms, timeslots, sessions };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `emploi-du-temps-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export JSON réussi');
  };

  const handleImportJSON = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const text = await file.text();
      try {
        const data = JSON.parse(text);
        // Importer les données (à implémenter selon les besoins)
        toast.success('Import JSON réussi');
        loadAllData();
      } catch (error) {
        toast.error('Erreur lors de l\'import JSON');
      }
    };
    input.click();
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-80' : 'w-0'} transition-all duration-300 overflow-hidden border-r bg-gray-50 flex flex-col no-print sidebar`}>
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Configuration</h2>
            <button className="btn btn-sm" onClick={() => setSidebarOpen(false)}>✕</button>
          </div>
          
          <div className="mb-4">
            <label className="label">Mode d'affichage</label>
            <select
              className="input w-full mb-2"
              value={viewMode}
              onChange={(e) => {
                setViewMode(e.target.value);
                setSelectedClassId('');
                setSelectedTeacherId('');
                setSelectedRoomId('');
              }}
            >
              <option value="class">Par classe</option>
              <option value="teacher">Par professeur</option>
              <option value="room">Par salle</option>
            </select>
            
            {viewMode === 'class' && (
              <select
                className="input w-full"
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
              >
                <option value="">— Toutes les classes —</option>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            
            {viewMode === 'teacher' && (
              <select
                className="input w-full"
                value={selectedTeacherId}
                onChange={(e) => setSelectedTeacherId(e.target.value)}
              >
                <option value="">— Tous les professeurs —</option>
                {teachers.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.first_name && t.last_name ? `${t.first_name} ${t.last_name}` : t.username}
                  </option>
                ))}
              </select>
            )}
            
            {viewMode === 'room' && (
              <select
                className="input w-full"
                value={selectedRoomId}
                onChange={(e) => setSelectedRoomId(e.target.value)}
              >
                <option value="">— Toutes les salles —</option>
                {rooms.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <SubjectsManager subjects={subjects} onRefresh={loadAllData} />
          <RoomsManager rooms={rooms} onRefresh={loadAllData} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="p-4 border-b bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {!sidebarOpen && (
                <button className="btn btn-sm" onClick={() => setSidebarOpen(true)}>
                  ☰
                </button>
              )}
              <div className="flex items-center gap-4">
                <h1 className="text-2xl font-bold">
                  Emploi du temps
                  {viewMode === 'class' && selectedClassId && (
                    <span className="text-lg font-normal text-gray-600 ml-2">
                      - {classes.find(c => String(c.id) === selectedClassId)?.name || ''}
                    </span>
                  )}
                  {viewMode === 'teacher' && selectedTeacherId && (
                    <span className="text-lg font-normal text-gray-600 ml-2">
                      - {(() => {
                        const t = teachers.find(t => String(t.id) === selectedTeacherId);
                        return t ? (t.first_name && t.last_name ? `${t.first_name} ${t.last_name}` : t.username) : '';
                      })()}
                    </span>
                  )}
                  {viewMode === 'room' && selectedRoomId && (
                  <span className="text-lg font-normal text-gray-600 ml-2">
                    - {rooms.find(r => String(r.id) === selectedRoomId)?.name || ''}
                  </span>
                  )}
                </h1>
                {/* Navigation entre les semaines */}
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    className="btn btn-sm"
                    onClick={() => setCurrentWeek(currentWeek - 1)}
                    title="Semaine précédente"
                  >
                    ←
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={() => setCurrentWeek(0)}
                    title="Semaine actuelle"
                  >
                    Aujourd'hui
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={() => setCurrentWeek(currentWeek + 1)}
                    title="Semaine suivante"
                  >
                    →
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="btn" onClick={handleExportJSON} title="Exporter JSON">
                <Download className="w-4 h-4" />
              </button>
              <button className="btn" onClick={handleImportJSON} title="Importer JSON">
                <Upload className="w-4 h-4" />
              </button>
              <button className="btn" onClick={handlePrint} title="Imprimer">
                <Printer className="w-4 h-4" />
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setModalDay(null);
                  setModalTimeslot(null);
                  setEditingSession(null);
                  setShowSessionModal(true);
                }}
              >
                <Plus className="w-4 h-4" /> Ajouter une séance
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4 edt-grid">
          {timeslots.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">Aucun créneau horaire défini.</p>
              <p className="text-sm text-gray-400">Ajoutez des créneaux dans le panneau de configuration.</p>
            </div>
          ) : (
            <EDTGrid
              sessions={sessions}
              timeslots={timeslots}
              classes={classes}
              subjects={subjects}
              teachers={teachers}
              rooms={rooms}
              onCellClick={handleCellClick}
              onSessionDragStart={handleSessionDragStart}
              onSessionDrop={handleSessionDrop}
              draggedSession={draggedSession}
              selectedClassId=""
              currentWeek={currentWeek}
              onResizeSession={handleResizeSession}
            />
          )}
        </div>
      </div>

      {/* Modal de séance */}
      <SessionModal
        open={showSessionModal}
        onClose={() => {
          setShowSessionModal(false);
          setEditingSession(null);
          setModalDay(null);
          setModalTimeslot(null);
        }}
        session={editingSession}
        onSave={handleSaveSession}
        onDelete={handleDeleteSession}
        classes={classes}
        subjects={subjects}
        teachers={teachers}
        rooms={rooms}
        timeslots={timeslots}
        dayOfWeek={modalDay}
        timeslotId={modalTimeslot}
      />
    </div>
  );
};

export default EmploisDuTemps;
